import { Button, Divider, IconButton, Input } from "@fullscreendigital/carrefour-dex-fe"
import { useLocation, useNavigate } from 'react-router-dom'
import { useNotification } from '../../../../../../../hooks'
import { useState } from 'react'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useMutation, useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../../utils/api'
import { URLS } from '../../../../constants'
import { CircularProgress, Grid, Typography } from '@mui/material'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import AlertModal from 'components/shop-reception/mobile/Common/AlertModal/AlertModal'
import { validateScanCode } from "../../../../helpers"
import { ManageSearch as ManageSearchIcon } from "@mui/icons-material"

const RegisterTruckStep2 = () => {
  const [supportCode, setSupportCode] = useState('')
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [scanInfo, setScanInfo] = useState(null)
  const [counter, setCounter] = useState(0)

  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()

  const { truckId } = location.state

  const { data: hubName, isLoading: isLoadingHubName } = useQuery(
    ['hub-name'],
    async () => {
      const response = await makeCoreGetRequest(`/shop-reception-mobile/home/hub-name`)
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor despre hub. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  const { data: truckInfo, isLoading: isLoadingTruckInfo } = useQuery(
    ['truck-info', truckId],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/truck/hub/summary/${truckId}`
      )
      setCounter(response.supportCounter)
      return response || {}
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor despre camion. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  const scan = async value => {
    if (!value) return

    const response = await makeCorePostRequest(
      `/shop-reception-mobile/support/hub/register/scan/${truckId}`,
      { supportCode: value }
    )
    if (!response.scanSuccess && response.message) {
      showNotification({
        message: `A aparut o eroare la cautarea codului. ${response.message}`,
        type: 'error'
      })
      document.getElementById('scan-input').focus()
      return
    } else {
      setScanInfo(response)
      setIsAlertOpen(true)
    }

    return response
  }

  const {
    isLoading: isLoadingScan,
    mutate: scanCode,
    data: scanCodeData,
    reset: resetScanData
  } = useMutation(scan, {
    onError: res => {
      showNotification({
        message: `A aparut o eroare la scanarea suportului. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const confirmSupport = async () => {
    const resp = await makeCorePostRequest(
      `/shop-reception-mobile/support/hub/add-in-truck/${truckId}`,
      {
        supportId: scanInfo.supportId
      }
    )

    setCounter(resp.supportCounter)
    setIsAlertOpen(false)
    setSupportCode('')
    document.getElementById('scan-input').focus()
  }

  const { isLoading: isConfirmSupportLoading, mutate } = useMutation(confirmSupport, {
    onSuccess: () => {
      showNotification({
        message: 'Confirmat.',
        type: 'success'
      })
      resetScanData()
      setSupportCode(null)
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const saveTruckData = async () => {
    await makeCorePostRequest('/shop-reception-mobile/support-traceability', {
      receptionTruckId: truckId,
      supportsNumber: null,
      inHubReleaseDateTime: null,
      inHubPresentationHour: null,
      inHubReleaseHour: null,
      inHubLoadingDateTime: new Date().toISOString(),
      inHubLoadingHour: new Date().toLocaleTimeString(),
      truckNumberHub: truckInfo?.truckNumber,
      driverName: null,
      loadedSupportsNumber: counter,
      inShopReleaseDate: null,
      inShopReleaseHour: null
    })
  }

  const { isLoading: isSaveDataLoading, mutate: saveData } = useMutation(saveTruckData, {
    onSuccess: () => {
      navigate(URLS.AUTH_REGISTER_TRUCK_STEP_3, { state: { truckId } })
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la salvarea datelor. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  if (isSaveDataLoading || isConfirmSupportLoading || isLoadingTruckInfo || isLoadingHubName) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  return (
    <Grid container spacing={2} justifyContent={'center'}>
      <Typography
        component="h2"
        variant="medium"
        style={{ textAlign: 'center', textTransform: 'uppercase' }}
        pt={2}
      >
        {hubName?.value}
      </Typography>

      <Grid xs={10} item>
        <Input
          type="text"
          variant="outlined"
          label="Cod suport"
          autoFocus
          value={supportCode}
          onChange={e => {
            const target = e.target as HTMLInputElement
            setSupportCode(target.value)
          }}
          onKeyUp={event => {
            const target = event.target as HTMLInputElement
            if (event.which == 0) {
              const currentValue = target.value
              if (currentValue && validateScanCode(showNotification, currentValue, 'suport')) {
                scanCode(currentValue)
                setSupportCode('')
              }
            }
          }}
          id="scan-input"
          width="100%"
        />
        <IconButton
          onClick={() => {
            if (validateScanCode(showNotification, supportCode, 'suport')) {
              scanCode(supportCode)
            }
          }}
        >
          <ManageSearchIcon />
        </IconButton>
      </Grid>

      <Grid item xs={10}>
        <Divider />
      </Grid>

      {counter > 0 && (
        <Grid xs={10} item>
          <Button
            variant="outlined"
            label="Suporti finalizati"
            onClick={() => saveData()}
            style={{ width: '100%' }}
            disabled={isSaveDataLoading}
          />
        </Grid>
      )}

      <Grid xs={10} item>
        <FormInfoItems
          items={[
            { label: 'Numar camion:', name: truckInfo?.truckNumber },
            { label: 'Numar suporti scanati:', name: counter }
          ]}
        />
      </Grid>

      <AlertModal
        open={isAlertOpen}
        handleClose={() => {
          setIsAlertOpen(false)
          setTimeout(() => {
            document.getElementById('scan-input').focus()
          }, 0)
        }}
        description={scanInfo?.message}
        buttons={[
          { label: 'Refuza',
            onClick: () => {
              setIsAlertOpen(false)
              setTimeout(() => {
                document.getElementById('scan-input').focus()
              }, 0)
            },
            color: 'primary' },
          {
            label: 'Expediaza',
            onClick: confirmSupport,
            color: 'primary'
          }
        ]}
      />
    </Grid>
  )
}

export default RegisterTruckStep2
